import { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  Home,
  LayoutDashboard,
  MessageSquare,
  MessagesSquare,
  Users,
  UserPlus,
  CalendarDays,
  UserCircle2,
  Megaphone,
  Workflow as WorkflowIcon,
  BookOpen,
  Settings,
  CalendarClock,
  type LucideIcon,
} from "lucide-react";

import { cn } from "lib/utils";
import { ScrollArea } from "components/ui/scroll-area";
import {
  Sheet,
  SheetContent,
  SheetTitle,
} from "components/ui/sheet";
import SidebarFooter from "./SidebarFooter";

type MenuItem = {
  to: string;
  icon: LucideIcon;
  label: string;
  group?: "Workspace" | "Tools" | "Admin";
  hidden?: boolean;
};

const buildMenuItems = (roleName: string): MenuItem[] => [
  { to: "/home", icon: Home, label: "Home", group: "Workspace", hidden: false },
  ...(roleName === "admin"
    ? ([{ to: "/dashboard", icon: LayoutDashboard, label: "Dashboard", group: "Workspace", hidden: false },] as MenuItem[])
    : []),
  { to: "/conversation", icon: MessageSquare, label: "Conversation", group: "Workspace", hidden: true },
  { to: "/communication", icon: MessagesSquare, label: "Communication", group: "Workspace", hidden: false },
  { to: "/friends", icon: UserPlus, label: "Friends", group: "Workspace", hidden: false },
  { to: "/contact", icon: Users, label: "Contact", group: "Workspace", hidden: false },
  { to: "/calendar", icon: CalendarDays, label: "Calendar", group: "Workspace", hidden: true },
  { to: "/campaign", icon: Megaphone, label: "Campaign", group: "Tools", hidden: true },
  { to: "/workflow", icon: WorkflowIcon, label: "Workflow", group: "Tools", hidden: true },
  { to: "/knowledge", icon: BookOpen, label: "Knowledge", group: "Tools", hidden: true },
  ...(roleName === "admin"
    ? ([{ to: "/schedule", icon: CalendarClock, label: "Staff Schedule", group: "Admin", hidden: true },
    { to: "/user", icon: UserCircle2, label: "User", group: "Workspace", hidden: false },
    { to: "/setting", icon: Settings, label: "Setting", group: "Admin", hidden: false }
    ] as MenuItem[])
    : []),
];

interface SidebarProps {
  mobileOpen: boolean;
  onMobileOpenChange: (open: boolean) => void;
}

const SidebarNav = ({
  items,
  pathname,
  onItemClick,
}: {
  items: MenuItem[];
  pathname: string;
  onItemClick?: () => void;
}) => {
  const grouped = useMemo(() => {
    const map = new Map<string, MenuItem[]>();
    items.forEach((item) => {
      if (item.hidden) return;
      const key = item.group ?? "Workspace";
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(item);
    });
    return Array.from(map.entries());
  }, [items]);

  return (
    <nav className="flex flex-col gap-6 px-3 py-4">
      {grouped.map(([group, groupItems]) => (
        <div key={group} className="flex flex-col gap-1">
          {/* <div className="px-3 pb-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground/80">
            {group}
          </div> */}
          {groupItems.map(({ to, icon: Icon, label }) => {
            const isActive =
              pathname === to || pathname.startsWith(`${to}/`);
            return (
              <Link
                key={to}
                to={to}
                onClick={onItemClick}
                className={cn(
                  "group relative flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors duration-200",
                  "text-muted-foreground hover:text-foreground",
                  isActive
                    ? "bg-primary/10 text-primary hover:text-primary"
                    : "hover:bg-muted/70"
                )}
              >
                {isActive && (
                  <span
                    aria-hidden
                    className="absolute inset-y-1 left-0 w-1 rounded-r-full bg-gradient-to-b from-primary to-accent"
                  />
                )}
                <Icon
                  className={cn(
                    "h-4 w-4 shrink-0 transition-colors",
                    isActive
                      ? "text-primary"
                      : "text-muted-foreground group-hover:text-foreground"
                  )}
                />
                <span className="truncate">{label}</span>
              </Link>
            );
          })}
        </div>
      ))}
    </nav>
  );
};

const Sidebar = ({ mobileOpen, onMobileOpenChange }: SidebarProps) => {
  const location = useLocation();
  const [roleName, setRoleName] = useState("user");

  useEffect(() => {
    if (location.pathname !== "/login") {
      setRoleName(localStorage.getItem("role") || "");
    }
  }, [location.pathname]);

  const items = useMemo(() => buildMenuItems(roleName), [roleName]);

  return (
    <>
      {/* Desktop sidebar */}
      <aside
        className={cn(
          "hidden lg:flex lg:flex-col",
          "h-[calc(100vh-4rem)] w-64 shrink-0",
          "border-r border-border bg-card"
        )}
      >
        <ScrollArea className="flex-1">
          <SidebarNav items={items} pathname={location.pathname} />
        </ScrollArea>
        <SidebarFooter />
      </aside>

      {/* Mobile drawer */}
      <Sheet open={mobileOpen} onOpenChange={onMobileOpenChange}>
        <SheetContent side="left" className="w-72 p-0">
          <SheetTitle className="sr-only">Navigation</SheetTitle>
          <div className="flex h-full flex-col pt-4">
            <ScrollArea className="flex-1">
              <SidebarNav
                items={items}
                pathname={location.pathname}
                onItemClick={() => onMobileOpenChange(false)}
              />
            </ScrollArea>
            <SidebarFooter />
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
};

export default Sidebar;
